// SurveyForm.tsx
import React, { useState, FormEvent, useEffect } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Typography,
  Box,
  Paper,
} from "@mui/material";
import {
  SurveyData,
  TeamOptionsBySport,
  GameType,
} from "../../types/quinipolo";
import MatchForm from "../../Components/MatchForm/MatchForm";
import { ReorderableMatchList } from "./components/ReorderableMatchList";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import locale from "antd/es/date-picker/locale/es_ES";
import { DatePicker } from "antd";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/es";
import styles from "./SurveyForm.module.scss";
import HowQuinipoloWorksModal from "./HowQuinipoloWorksModal";
import { useNavigate } from "react-router-dom";
import { useFeedback } from "../../Context/FeedbackContext/FeedbackContext";
import { useTranslation } from "react-i18next";
import { apiGet, apiPost } from "../../utils/apiUtils";
import {
  isFinishedLeagueApiError,
  isLeagueFinished,
} from "../../utils/leagueStatus";
import { MatchAutoFillModal } from "./MatchAutoFillModal";
import { ScraperMatchV2 } from "../../services/scraper/types";
import { useUser } from "../../Context/UserContext/UserContext";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ScienceIcon from "@mui/icons-material/Science";
import SortIcon from "@mui/icons-material/Sort";
import { sortMatchesByLeague } from "./utils/leagueOrder";

const isDev = process.env.NODE_ENV === "development";

const SurveyForm = () => {
  const navigate = useNavigate();
  const { setFeedback } = useFeedback();
  const { userData } = useUser();

  // Initialize quinipolo with 15 empty matches
  const [quinipolo, setQuinipolo] = useState<SurveyData[]>(
    new Array(15).fill(null).map((_, index) => ({
      gameType: "waterpolo",
      homeTeam: "",
      awayTeam: "",
      date: null,
      isGame15: index === 14,
    })),
  );

  const [loading, setLoading] = useState<boolean>(false);
  const [autoFillModalOpen, setAutoFillModalOpen] = useState<boolean>(false);
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [teamOptions, setTeamOptions] = useState<TeamOptionsBySport>({
    waterpolo: [],
    football: [],
    basketball: [],
    handball: [],
    tennis: [],
  });
  const [helpModalOpen, setHelpModalOpen] = useState<boolean>(false);
  const [allowRepeatedTeams, setAllowRepeatedTeams] = useState<boolean>(true);
  const [matchErrors, setMatchErrors] = useState<Record<number, string | null>>(
    {},
  );
  const [isMatch15Locked, setIsMatch15Locked] = useState<boolean>(true);

  const queryParams = new URLSearchParams(window.location.search);
  // Check if this is for all leagues
  const isForAllLeagues = queryParams.get("allLeagues") === "true";

  // Check if this is for managed leagues only
  const isForManagedLeagues = queryParams.get("managedLeagues") === "true";

  const leagueId = queryParams.get("leagueId");
  const needsFinishedCheck =
    !isForAllLeagues && !isForManagedLeagues && Boolean(leagueId);
  const [leagueGate, setLeagueGate] = useState<"checking" | "open" | "finished">(
    needsFinishedCheck ? "checking" : "open"
  );

  const selectedTeams = quinipolo
    .map((match) => match.awayTeam)
    .concat(quinipolo.map((match) => match.homeTeam));
  const { t } = useTranslation();

  const handleDateChange: (
    date: Dayjs | null,
    dateString: string | string[],
  ) => void = (date) => {
    setSelectedDate(date);
  };

  const handleHelpClick = () => {
    setHelpModalOpen(true);
  };

  const handleCloseHelpModal = () => {
    setHelpModalOpen(false);
  };

  useEffect(() => {
    if (!needsFinishedCheck || !leagueId) {
      setLeagueGate("open");
      return;
    }
    let cancelled = false;
    setLeagueGate("checking");
    apiGet<{ status?: string }>(`/api/leagues/${leagueId}`)
      .then((league) => {
        if (cancelled) return;
        setLeagueGate(isLeagueFinished(league) ? "finished" : "open");
      })
      .catch(() => {
        // Leave creation available; the API still rejects finished leagues.
        if (!cancelled) setLeagueGate("open");
      });
    return () => {
      cancelled = true;
    };
  }, [needsFinishedCheck, leagueId]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    try {
      if (leagueGate === "finished") {
        setFeedback({
          message: t("errorCreatingQuinipoloFinished"),
          severity: "info",
          open: true,
        });
        return;
      }
      if (hasBlockingErrors) {
        setFeedback({
          message: t("genderMismatchSubmitError"),
          severity: "error",
          open: true,
        });
        window.scrollTo(0, 0);
        return;
      }

      if (
        selectedDate === null ||
        !selectedDate.isValid() ||
        selectedDate.toDate() < new Date()
      ) {
        setFeedback({
          message: t("selectDateTimeForQuinipolo"),
          severity: "error",
          open: true,
        });
        window.scrollTo(0, 0);
        return;
      }

      // Set loading state to prevent multiple submissions
      setLoading(true);

      // Build correct_answers for match 15 if goals were entered
      const match15 = quinipolo[14];
      const hasMatch15Goals = match15?.goalsHomeTeam && match15?.goalsAwayTeam;
      const correctAnswers = hasMatch15Goals
        ? Array.from({ length: 15 }, (_, i) => ({
            matchNumber: i + 1,
            chosenWinner: i === 14 ? match15.homeTeam : "",
            goalsHomeTeam: i === 14 ? match15.goalsHomeTeam : "",
            goalsAwayTeam: i === 14 ? match15.goalsAwayTeam : "",
            isGame15: i === 14,
          }))
        : [];

      // Determine the API endpoint based on the type of creation
      let endpoint = "/api/quinipolos";
      let requestBody: any = {
        league_id: leagueId,
        quinipolo,
        end_date: selectedDate.toDate(),
        creation_date: new Date(),
        ...(correctAnswers.length > 0 && { correct_answers: correctAnswers }),
      };

      if (isForAllLeagues) {
        endpoint = "/api/quinipolos/all-leagues";
        requestBody = {
          quinipolo,
          end_date: selectedDate.toDate(),
          creation_date: new Date(),
          ...(correctAnswers.length > 0 && { correct_answers: correctAnswers }),
        };
      } else if (isForManagedLeagues) {
        endpoint = "/api/quinipolos/managed-leagues";
        requestBody = {
          quinipolo,
          end_date: selectedDate.toDate(),
          creation_date: new Date(),
          ...(correctAnswers.length > 0 && { correct_answers: correctAnswers }),
        };
      }

      // Create quinipolo via backend API
      const response = await apiPost<any>(endpoint, requestBody);

      let successMessage = t("quinipoloCreatedSuccess");
      if (isForAllLeagues) {
        successMessage = `Successfully created quinipolos for ${response.successfulCreations} out of ${response.totalLeagues} leagues`;
      } else if (isForManagedLeagues) {
        successMessage = `Successfully created quinipolos for ${response.successfulCreations} out of ${response.totalLeagues} managed leagues`;
      }

      setFeedback({
        message: successMessage,
        severity: "success",
        open: true,
      });

      // Navigate based on the type of creation
      if (isForAllLeagues || isForManagedLeagues) {
        navigate("/dashboard");
      } else {
        navigate("/quinipolo-success", { state: { quinipolo: response } });
      }
    } catch (error) {
      if (isFinishedLeagueApiError(error)) {
        if (!isForAllLeagues && !isForManagedLeagues) {
          setLeagueGate("finished");
        }
        setFeedback({
          message: t("errorCreatingQuinipoloFinished"),
          severity: "info",
          open: true,
        });
      } else {
        setFeedback({
          message: t("errorCreatingQuinipolo"),
          severity: "error",
          open: true,
        });
      }
      console.error("Error creating Quinipolo:", error);
    } finally {
      // Always reset loading state, even if there's an error
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);

    const fetchTeams = async () => {
      try {
        // Use only the backend API since Supabase teams calls are failing
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/teams/all`,
        );
        if (!response.ok) {
          throw new Error(`Backend API failed: ${response.status}`);
        }
        const backendTeams = await response.json();
        const normalizedTeams: TeamOptionsBySport = {
          waterpolo: [],
          football: [],
          basketball: [],
          handball: [],
          tennis: [],
        };
        for (const sport of Object.keys(backendTeams) as GameType[]) {
          normalizedTeams[sport] = Array.isArray(backendTeams[sport])
            ? backendTeams[sport].map((team: any) => ({
                name: typeof team === "string" ? team : team.name,
                sport,
                gender: team?.gender ?? null,
                aliases: Array.isArray(team?.alias)
                  ? team.alias
                  : Array.isArray(team?.aliases)
                    ? team.aliases
                    : [],
              }))
            : [];
        }
        setTeamOptions(normalizedTeams);
      } catch (error) {
        console.error("Error fetching teams:", error);
        setFeedback({
          message: "Error loading teams",
          severity: "error",
          open: true,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchTeams();
  }, [setFeedback]);

  const matchArray = new Array(15).fill(null);

  const handleMatchValidationChange = (
    matchIndex: number,
    error: string | null,
  ) => {
    setMatchErrors((prev) => {
      if (prev[matchIndex] === error) {
        return prev;
      }
      return { ...prev, [matchIndex]: error };
    });
  };

  const handleFillMockData = async () => {
    const { MOCK_SURVEY_DATA } = await import("./SurveyForm.dev");
    setQuinipolo(MOCK_SURVEY_DATA);
    setMatchErrors({});
    setSelectedDate(dayjs().add(1, "day").hour(23).minute(59).second(0));
    setFeedback({
      message: t("autoFillSuccess"),
      severity: "success",
      open: true,
    });
  };

  const handleOrderByLeague = () => {
    const sorted = sortMatchesByLeague(quinipolo);
    setQuinipolo(
      sorted.map((match, idx) => ({
        ...match,
        isGame15: idx === 14,
      })),
    );
    setFeedback({
      message: t("orderByLeagueSuccess"),
      severity: "success",
      open: true,
    });
  };

  const handleAutoFillConfirm = ({
    matches,
    plenoMatchId,
  }: {
    matches: ScraperMatchV2[];
    plenoMatchId: string | null;
  }) => {
    const converted = buildSurveyDataFromSelection(matches, plenoMatchId);
    const ordered = sortMatchesByLeague(converted).map((match, idx) => ({
      ...match,
      isGame15: idx === 14,
    }));
    setQuinipolo(ordered);
    setMatchErrors({});

    if (matches.length === 15) {
      const earliest = matches
        .map((match) => dayjs(match.startTime))
        .sort((a, b) => a.valueOf() - b.valueOf())[0];
      setSelectedDate(earliest && earliest.isValid() ? earliest : null);
    } else {
      setSelectedDate(null);
    }

    setFeedback({
      message: t("autoFillSuccess"),
      severity: "success",
      open: true,
    });
  };

  const hasBlockingErrors = Object.values(matchErrors).some(Boolean);

  // Check if user has scraper access
  const hasScraperAccess = userData.hasScraperAccess === true;

  if (leagueGate === "checking") {
    return (
      <div className={styles.form} style={{ display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </div>
    );
  }

  if (leagueGate === "finished") {
    return (
      <div className={styles.form}>
        <h2>{t("createQuinipolo")}</h2>
        <Alert severity="info" sx={{ mt: 2 }}>
          {t("leagueFinishedCreateBlocked")}
        </Alert>
        <Button sx={{ mt: 3 }} variant="outlined" onClick={() => navigate(-1)}>
          {t("backBtn")}
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      <div
        style={{
          color: "white",
          display: "flex",
          // alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 20,
        }}
      >
        <h2>{t("createQuinipolo")}</h2>
        <HelpOutlineRoundedIcon
          onClick={handleHelpClick}
          style={{ cursor: "pointer" }}
        />
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 8,
          marginBottom: 20,
        }}
      >
        {isDev && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<ScienceIcon />}
            onClick={handleFillMockData}
            disabled={loading}
            sx={{
              width: { xs: "100%", sm: "auto" },
              borderColor: "rgba(255,255,255,0.5)",
              color: "rgba(255,255,255,0.9)",
              "&:hover": {
                borderColor: "rgba(255,255,255,0.8)",
                backgroundColor: "rgba(255,255,255,0.08)",
              },
            }}
          >
            Fill mock (dev)
          </Button>
        )}

        {hasScraperAccess && (
          <Button
            variant="contained"
            startIcon={<AutoAwesomeIcon />}
            onClick={() => setAutoFillModalOpen(true)}
            disabled={loading}
            sx={{
              width: { xs: "100%", sm: "auto" },
              background: "linear-gradient(135deg, #b8860b, #ffd54f)",
              color: "#3e2723",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              "&:hover": {
                background: "linear-gradient(135deg, #a07007, #ffca28)",
                color: "#3e2723",
              },
              "&.Mui-disabled": {
                background:
                  "linear-gradient(135deg, rgba(184,134,11,0.4), rgba(255,213,79,0.4))",
                color: "rgba(62,39,35,0.6)",
              },
            }}
          >
            {t("autoFillSurvey")}
          </Button>
        )}
      </div>
      <p className={styles.dateTimeDisclaimer}>{t("dateTimeDisclaimer")}</p>
      <div className={styles.datePickerContainer}>
        <DatePicker
          format="DD/MM/YYYY HH:mm"
          onChange={handleDateChange}
          locale={locale}
          placeholder={t("date")}
          className={styles.datePicker}
          showNow={false}
          popupClassName={styles.datePickerPopup}
          showTime={{ format: "HH:mm" }}
          value={selectedDate}
        />
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          marginTop: 16,
          marginBottom: 8,
          gap: 8,
        }}
      >
        <Button
          variant="outlined"
          size="small"
          startIcon={<SortIcon />}
          onClick={handleOrderByLeague}
          disabled={loading}
          sx={{
            width: { xs: "100%", sm: "auto" },
            borderColor: "rgba(255,255,255,0.5)",
            color: "rgba(255,255,255,0.9)",
            "&:hover": {
              borderColor: "rgba(255,255,255,0.8)",
              backgroundColor: "rgba(255,255,255,0.08)",
            },
          }}
        >
          {t("orderByLeague")}
        </Button>
      </div>
      <ReorderableMatchList
        quinipolo={quinipolo}
        setQuinipolo={setQuinipolo}
        teamOptions={teamOptions}
        selectedTeams={allowRepeatedTeams ? [] : selectedTeams}
        loading={loading}
        allowRepeatedTeams={allowRepeatedTeams}
        isMatch15Locked={isMatch15Locked}
        setIsMatch15Locked={setIsMatch15Locked}
        onValidationChange={handleMatchValidationChange}
        matchErrors={matchErrors}
      />

      <div className={styles.submitButton}>
        <Button
          type="submit"
          disabled={loading || hasBlockingErrors}
          variant="contained"
          color="primary"
        >
          {loading ? t("creatingQuinipolo") : t("createQuinipolo")}
        </Button>
      </div>

      {/* Help Modal */}
      <HowQuinipoloWorksModal
        open={helpModalOpen}
        onClose={handleCloseHelpModal}
      />
      <MatchAutoFillModal
        open={autoFillModalOpen}
        onClose={() => setAutoFillModalOpen(false)}
        onConfirm={({ matches, plenoMatchId }) => {
          handleAutoFillConfirm({ matches, plenoMatchId });
          setAutoFillModalOpen(false);
        }}
      />
    </form>
  );
};

export default SurveyForm;

function buildSurveyDataFromSelection(
  matches: ScraperMatchV2[],
  plenoMatchId: string | null,
): SurveyData[] {
  if (!matches.length) {
    return new Array(15).fill(null).map((_, index) => ({
      gameType: "waterpolo",
      homeTeam: "",
      awayTeam: "",
      date: new Date(),
      isGame15: index === 14,
    }));
  }

  const sorted = [...matches].sort(
    (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
  );

  if (plenoMatchId) {
    const plenoIndex = sorted.findIndex(
      (match) => match.matchId === plenoMatchId,
    );
    if (plenoIndex > -1) {
      const [plenoMatch] = sorted.splice(plenoIndex, 1);
      sorted.push(plenoMatch);
    }
  }

  const surveyMatches: SurveyData[] = sorted.slice(0, 15).map(() => ({
    gameType: "waterpolo",
    homeTeam: "",
    awayTeam: "",
    date: new Date(),
    isGame15: false,
  }));

  sorted.slice(0, 15).forEach((match, index) => {
    surveyMatches[index] = {
      gameType: "waterpolo",
      homeTeam: match.homeTeam,
      awayTeam: match.awayTeam,
      date: new Date(match.startTime),
      isGame15: false,
      leagueId: match.leagueId,
      leagueName: match.leagueName,
    };
  });

  while (surveyMatches.length < 15) {
    surveyMatches.push({
      gameType: "waterpolo",
      homeTeam: "",
      awayTeam: "",
      date: new Date(),
      isGame15: false,
    });
  }

  if (surveyMatches.length > 0) {
    surveyMatches.forEach((match, index) => {
      match.isGame15 = index === surveyMatches.length - 1;
    });
  }

  return surveyMatches;
}
